/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
'use client';

import { useMemo, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronUp } from 'lucide-react';
import type { ShopperProducts, ShopperPromotions } from '@/scapi';

import { CartExtendToggle } from '@/components/cart/cart-extend-toggle';
import KitQuantityPicker from '@/components/cart/kit-quantity-picker';
import { Link } from '@/components/link';
import CartLinePromoCallouts from '@/components/product-price/cart-line-promo-callouts';
import { Typography } from '@/components/typography';
import { useSite } from '@salesforce/storefront-next-runtime/site-context';
import { formatCurrency } from '@/lib/currency';
import { createProductUrl, type EnrichedProductItem } from '@/lib/product/product-utils';
import { getKitGroupPricing, type KitGroup } from '@/lib/kit-utils';
import KitGroupImage from '@/components/product-kit/kit-group-image';
import { KitItemVariations } from '@/components/product-kit/kit-item-variations';

interface KitGroupCardProps {
    /** The basket-level kit group descriptor (`c_kitGroups[]`). */
    group: KitGroup;
    /** The exploded component line items belonging to this kit instance, in basket order. */
    items: EnrichedProductItem[];
    /**
     * Same Save-for-later / Remove render prop the standard `<ProductItem>`
     * receives, so a kit row carries the identical secondary actions. It is
     * invoked with a representative line item anchored on the kit's first
     * component (Remove cascades to the whole kit server-side).
     */
    secondaryActions?: (product: EnrichedProductItem) => ReactElement | undefined;
    /** Optional kit-master product payload used for the hero thumbnail. */
    masterProduct?: ShopperProducts.schemas['Product'];
    /**
     * SSF-390 Issue 6 — Promotion details keyed by `promotionId`. Passed
     * through to `<CartLinePromoCallouts>` so the kit callout can prefer the
     * richer customer-facing `offerMsg` / `calloutMsg` from the Shopper
     * Promotions API. Optional — the callout still renders on the merchant-
     * authored `itemText` fallback when the map is missing.
     */
    promotions?: Record<string, ShopperPromotions.schemas['Promotion']>;
}

/**
 * CART-19 — renders a kit/package as a SINGLE cart line item, matching the
 * standard line-item treatment (image, name, roll-up price, Extend toggle,
 * Save-for-later / Remove, a "Product Includes" list, and a quantity picker) so
 * all kits render consistently with the rest of the cart.
 *
 * The backend explodes a kit master into one PLI per component (sharing one
 * `partOfKitNumber`); this card collapses those PLIs back into one visual line:
 *   - image / name      → the master (via the first component + `group.name`)
 *   - price             → `group.lineTotal` (Σ component adjusted prices)
 *   - quantity          → kit instance count, edited via `<KitQuantityPicker>`
 *                         which scales every component server-side
 *   - Extend            → keyed on the master product id; one offer per kit
 *   - Product Includes  → the component list (read-only; the kit is atomic)
 *   - Remove            → cascades to every component (grouped by partOfKitNumber)
 *
 * The underlying explosion / roll-up pricing model is intentionally left
 * unchanged — this is a presentation-only collapse.
 */
export default function KitGroupCard({
    group,
    items,
    secondaryActions,
    masterProduct,
    promotions,
}: KitGroupCardProps): ReactElement | null {
    const { t, i18n } = useTranslation('cart');
    const { currency } = useSite();
    // "Product Includes" list defaults to expanded, matching <BundledProductItems> (CART-02 spec).
    const [includesExpanded, setIncludesExpanded] = useState(true);

    const anchor = items[0];
    const anchorItemId = group.itemIds[0] ?? anchor?.itemId ?? '';
    const quantity = group.quantity || 1;

    // SSF-390 Issue 6 — aggregate the kit-level pre-promo total and promo
    // callouts from the component PLIs. `group.lineTotal` on the SCAPI response
    // is already the DISCOUNTED roll-up, so on its own the card can't show the
    // strikethrough or the "Save $X" message. `getKitGroupPricing` walks each
    // component's `basePrice × quantity` (pre-promo) and dedupes their
    // `priceAdjustments[]` by promotion so the card renders one strikethrough
    // + one callout per applied promo instead of N repeats.
    const pricing = useMemo(() => getKitGroupPricing(group, items), [group, items]);

    // A representative line item for the kit, used by the shared secondary
    // actions (Save-for-later / Remove) and the Extend toggle. It is anchored on
    // the first component PLI (so Remove → cascade) but presents the MASTER
    // product id + name (so Extend looks up the master's offer and one offer
    // shows per kit, matching legacy `extendOBOHelpers` kit dedup).
    const representativeItem = useMemo<EnrichedProductItem>(
        () =>
            ({
                ...anchor,
                itemId: anchorItemId,
                productId: group.masterId ?? anchor?.productId,
                productName: group.name ?? anchor?.productName ?? anchor?.name,
                quantity,
            }) as EnrichedProductItem,
        [anchor, anchorItemId, group.masterId, group.name, quantity]
    );

    // SSF-390 Issue 2 — the "Product Includes" list must show each component's
    // PER-KIT contribution (`c_apkQuantity`), NOT the exploded PLI quantity.
    // On the SCAPI basket a component PLI's `quantity` is `apkQuantity × kit
    // instances` (see `KitExplosionHandler.js`), so reading `item.quantity`
    // directly makes the includes list scale up every time the shopper bumps
    // the kit stepper — e.g. a kit of 4 chairs at qty 2 renders "Quantity 8"
    // for the chair row, which is misleading (the kit still contains 4 chairs
    // per instance; there are just two instances of the kit).
    //
    // Prefer the enriched `c_apkQuantity`. If enrichment is missing on a stale
    // PLI, recover the per-kit multiplier by dividing the scaled PLI quantity
    // by the kit instance count (`group.quantity`), which is what the server
    // used to scale it in the first place. Floor at 1 so the row never renders
    // "Quantity 0".
    const kitInstances = group.quantity > 0 ? group.quantity : 1;
    const includesItems = useMemo(
        () =>
            items.map((item) => {
                const perKit =
                    item.c_apkQuantity ??
                    (typeof item.quantity === 'number' ? Math.max(1, Math.round(item.quantity / kitInstances)) : 1);
                return {
                    key: item.itemId,
                    label: item.productName || item.name || item.productId || '',
                    quantity: perKit,
                };
            }),
        [items, kitInstances]
    );

    if (items.length === 0) {
        return null;
    }

    const title = group.name || anchor?.productName || anchor?.name || '';
    const visualItem = masterProduct ? ({ ...masterProduct, productName: title } as EnrichedProductItem) : anchor;
    const perKitTotal = quantity > 0 ? group.lineTotal / quantity : group.lineTotal;
    // SSF-390 Issue 7 — kit title routes to the kit master PDP. Anchor on
    // `group.masterId` (the resolved virtual master id), not the first
    // component PLI, so the shopper lands on the same PDP that owns the
    // kit's roll-up price and Extend offer. Fall back to `#` via
    // `createProductUrl` when the master id is missing (empty kit — should
    // never render, but keeps the anchor navigable).
    const kitMasterUrl = createProductUrl(group.masterId ?? undefined, null, 'color', null, title);

    return (
        <div
            data-testid={`sf-kit-group-${group.masterId ?? group.kitNumber}`}
            className="relative overflow-hidden px-0 py-6 md:px-0 md:py-8">
            <div className="grid min-w-0 grid-cols-[112px_minmax(0,1fr)] gap-4 md:grid-cols-[160px_minmax(0,1fr)] md:gap-6">
                <div className="flex flex-shrink-0 items-start justify-center">
                    <Link to={kitMasterUrl} aria-label={title} className="block">
                        <KitGroupImage
                            productItem={visualItem}
                            title={title}
                            containerClassName="w-28 md:w-40 aspect-square overflow-hidden rounded-sm"
                        />
                    </Link>
                </div>

                <div className="min-w-0 flex-1 space-y-3">
                    <div className="grid min-w-0 grid-cols-1 gap-4 md:grid-cols-[minmax(0,1fr)_180px] md:gap-6">
                        <div className="min-w-0">
                            <h3 className="text-base font-semibold leading-6 text-foreground md:text-lg">
                                <Link to={kitMasterUrl} className="hover:underline">
                                    {title}
                                </Link>
                            </h3>
                            <div className="mt-1.5 flex flex-wrap items-baseline gap-2">
                                {pricing.hasSavings ? (
                                    <span
                                        className="text-sm font-normal leading-7 text-muted-foreground line-through"
                                        data-testid="sf-kit-group-list-price">
                                        {formatCurrency(pricing.originalLineTotal, i18n.language, currency)}
                                    </span>
                                ) : null}
                                <span
                                    className="text-lg font-normal leading-7 text-foreground"
                                    data-testid="sf-kit-group-current-price">
                                    {formatCurrency(group.lineTotal, i18n.language, currency)}
                                </span>
                            </div>
                            {pricing.hasSavings ? (
                                <CartLinePromoCallouts
                                    priceAdjustments={pricing.promoAdjustments}
                                    promotions={promotions}
                                    className="mt-1"
                                />
                            ) : null}
                            {/* Item ID — mirrors the standard <ProductItem>
                                "Item · <productId>" row so kits render the
                                same identifier line as every other cart line.
                                Anchor on the kit master id (`group.masterId`);
                                fall back to the anchor component's productId
                                so the row still renders on the (unlikely)
                                stale-basket case where the master id is
                                missing but a component id is present. */}
                            {(group.masterId ?? anchor?.productId) ? (
                                <div
                                    className="mt-1.5 text-xs text-muted-foreground md:text-sm"
                                    data-testid="sf-kit-group-item-id">
                                    {t('attributes.itemId')} · {group.masterId ?? anchor?.productId}
                                </div>
                            ) : null}
                            <KitItemVariations items={items} masterProduct={masterProduct} kitTitle={title} />
                        </div>

                        <div className="grid content-start justify-items-start gap-3 md:justify-items-end md:gap-4">
                            <div className="w-full md:w-[112px]">
                                <KitQuantityPicker anchorItemId={anchorItemId} value={quantity} />
                            </div>
                            {quantity > 1 ? (
                                <Typography variant="small" className="text-xs text-muted-foreground">
                                    {`${formatCurrency(perKitTotal, i18n.language, currency)}/each`}
                                </Typography>
                            ) : null}
                        </div>
                    </div>

                    <div className="mt-3 w-full space-y-3">
                        {secondaryActions ? <div className="w-full">{secondaryActions(representativeItem)}</div> : null}

                        {/* Extend protection-plan toggle, keyed on the kit master.
                            Self-hides unless the cart-route offers map carries an
                            offer for the master product id. */}
                        <CartExtendToggle product={representativeItem} className="w-full" />

                        {/* "Product Includes" — the kit's components (read-only),
                            rendered with the same collapsible treatment as a
                            standard bundle's <BundledProductItems> so kits and
                            SCAPI bundles look identical in the cart. */}
                        {includesItems.length > 0 ? (
                            <div className="mt-2 space-y-2" data-testid="sf-kit-product-includes">
                                <button
                                    type="button"
                                    className="flex w-full items-center justify-between text-sm font-medium text-foreground"
                                    aria-expanded={includesExpanded}
                                    onClick={() => setIncludesExpanded((prev) => !prev)}>
                                    <span>{`${t('bundle.productIncludes')}:`}</span>
                                    {includesExpanded ? (
                                        <ChevronUp className="h-4 w-4 shrink-0" aria-hidden />
                                    ) : (
                                        <ChevronDown className="h-4 w-4 shrink-0" aria-hidden />
                                    )}
                                </button>

                                {includesExpanded ? (
                                    <div className="space-y-2 pl-2">
                                        {includesItems.map((item) => (
                                            <div key={item.key} className="space-y-0.5">
                                                <div className="text-sm text-foreground">{item.label}</div>
                                                <div className="text-sm text-muted-foreground">
                                                    {`${t('attributes.quantity')} ${item.quantity}`}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : null}
                            </div>
                        ) : null}
                    </div>
                </div>
            </div>
        </div>
    );
}
